import { useState } from 'react';
import { useConsent } from '@/contexts/CookieConsentContext';

interface TierToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function TierToggle({ label, description, checked, onChange, disabled }: TierToggleProps) {
  return (
    <label className="flex items-start gap-3 py-3 cursor-pointer select-none">
      <span className="relative inline-flex items-center mt-0.5 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <span
          className={`w-10 h-6 rounded-full transition-colors ${
            disabled
              ? 'bg-primary/60 cursor-not-allowed'
              : checked
                ? 'bg-primary'
                : 'bg-gray-300'
          } peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-2`}
        />
        <span
          className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </span>
      <span>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        <span className="block text-sm text-gray-500 leading-relaxed">{description}</span>
      </span>
    </label>
  );
}

export default function CookieConsentSettings() {
  const { consent, saveCustom, closeSettings, acceptAll, rejectAll } = useConsent();

  const [tier2, setTier2] = useState(consent?.tier2 ?? false);
  const [tier3, setTier3] = useState(consent?.tier3 ?? false);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie preferences"
      className="fixed bottom-5 right-5 z-[9990] max-w-md w-full bg-white border border-gray-200 rounded-xl shadow-overlay animate-in slide-in-from-bottom-4"
    >
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-base font-bold text-gray-900 mb-1">Cookie preferences</h2>
        <p className="text-sm text-gray-500 leading-relaxed">
          Choose which cookies you'd like to allow.{' '}
          <a href="/cookies" className="text-primary hover:underline font-medium">
            Cookie Policy
          </a>
        </p>
      </div>

      <div className="px-6 py-2 divide-y divide-gray-100">
        <TierToggle
          label="Strictly Necessary"
          description="Essential for the site to work. Always on."
          checked={true}
          onChange={() => {}}
          disabled
        />
        <TierToggle
          label="Functional"
          description="Live chat support, accessibility tools, and error monitoring."
          checked={tier2}
          onChange={setTier2}
        />
        <TierToggle
          label="Marketing and Analytics"
          description="Helps us understand how you use the site and measure ad performance."
          checked={tier3}
          onChange={setTier3}
        />
      </div>

      <div className="p-6 pt-4 flex flex-wrap gap-3">
        <button
          onClick={() => saveCustom(tier2, tier3)}
          className="flex-1 min-w-[120px] px-4 py-2.5 bg-primary text-white rounded-sm font-bold text-sm hover:bg-black/50 transition-colors"
        >
          Save Preferences
        </button>
        <button
          onClick={acceptAll}
          className="flex-1 min-w-[100px] px-4 py-2.5 border border-gray-300 text-gray-700 rounded-sm font-bold text-sm hover:bg-gray-50 transition-colors"
        >
          Accept All
        </button>
        <button
          onClick={rejectAll}
          className="flex-1 min-w-[100px] px-4 py-2.5 border border-gray-300 text-gray-700 rounded-sm font-bold text-sm hover:bg-gray-50 transition-colors"
        >
          Reject All
        </button>
      </div>
    </div>
  );
}
